import React, { useState } from 'react';
import { Box, Typography, Container, Grid } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

const FEATURES = [
  {
    num: '01',
    title: 'Suministro Constante',
    description: 'Infraestructura monitoreada en tiempo real para garantizar flujo ininterrumpido a cada hogar de la comunidad.',
    icon: <WaterDropOutlinedIcon />,
  },
  {
    num: '02',
    title: 'Analítica de Consumo',
    description: 'Métricas detalladas y reportes históricos de consumo accesibles desde cualquier dispositivo en cualquier momento.',
    icon: <QueryStatsIcon />,
  },
  {
    num: '03',
    title: 'Transparencia Total',
    description: 'Gestión administrativa digitalizada para una rendición de cuentas clara, con procesos completamente auditables.',
    icon: <VerifiedOutlinedIcon />,
  },
  {
    num: '04',
    title: 'Atención Permanente',
    description: 'Soporte especializado para reportes y mantenimiento, asegurando la calidad continua del recurso hídrico.',
    icon: <SupportAgentOutlinedIcon />,
  },
];

const LandingFeatures = () => {
  const theme = useTheme();
  const [active, setActive] = useState(null);

  return (
    <Box id="features" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#FFFFFF' }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 12 }} sx={{ alignItems: 'flex-start' }}>

          {/* Sticky label */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
              <Typography
                variant="overline"
                sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 2.5, fontSize: '0.7rem' }}
              >
                SERVICIOS
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  mt: 1.5,
                  color: 'text.primary',
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                }}
              >
                Lo que hacemos por el agua
              </Typography>
              <Box sx={{ mt: 3, width: 40, height: 3, borderRadius: 2, bgcolor: 'primary.main' }} />
              <Typography
                variant="body2"
                sx={{ mt: 3, color: 'text.secondary', lineHeight: 1.8, fontSize: '0.9rem' }}
              >
                Combinamos infraestructura robusta con tecnología moderna para administrar el recurso hídrico con eficiencia y transparencia.
              </Typography>
            </Box>
          </Grid>

          {/* Numbered rows */}
          <Grid size={{ xs: 12, md: 8 }}>
            {FEATURES.map((f, i) => (
              <Box
                key={i}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                sx={{
                  display: 'flex',
                  gap: { xs: 2.5, md: 4 },
                  alignItems: 'flex-start',
                  py: 4,
                  borderBottom: '1px solid',
                  borderTop: i === 0 ? '1px solid' : 'none',
                  borderColor: active === i ? 'transparent' : 'divider',
                  cursor: 'default',
                  mx: { md: -3 },
                  px: { md: 3 },
                  borderRadius: '12px',
                  transition: 'background 0.2s, border-color 0.2s',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.lighter, 0.5) },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: '0.8rem',
                    color: active === i ? 'primary.main' : 'rgba(0,0,0,0.2)',
                    letterSpacing: 1,
                    mt: '3px',
                    minWidth: 28,
                    transition: 'color 0.2s',
                  }}
                >
                  {f.num}
                </Typography>

                <Box
                  sx={{
                    flexShrink: 0,
                    width: 44, height: 44,
                    borderRadius: '10px',
                    bgcolor: active === i ? 'primary.main' : alpha(theme.palette.primary.main, 0.07),
                    color: active === i ? 'primary.contrastText' : 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {React.cloneElement(f.icon, { sx: { fontSize: 22 } })}
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1rem', mb: 0.75, lineHeight: 1.3 }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.9rem' }}
                  >
                    {f.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingFeatures;
